import { useState, useEffect } from 'react';
import { blogService } from '../services/apiService';
import { getImageSrc } from '../utils/imageHelper';

const BlogForm = ({ blog, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    content: [''],
    videoLink: '',
    externalLink: '',
    publishDate: new Date().toISOString().split('T')[0],
    author: 'Admin',
    tags: ''
  });
  const [thumbnail, setThumbnail] = useState(null);
  const [additionalImages, setAdditionalImages] = useState([]);
  const [pdfFile, setPdfFile] = useState(null);
  const [removePdf, setRemovePdf] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (blog) {
      setFormData({
        title: blog.title,
        content: blog.content,
        videoLink: blog.videoLink || '',
        externalLink: blog.externalLink || '',
        publishDate: new Date(blog.publishDate).toISOString().split('T')[0],
        author: blog.author,
        tags: blog.tags.join(', ')
      });
    }
  }, [blog]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleContentChange = (index, value) => {
    const newContent = [...formData.content];
    newContent[index] = value;
    setFormData(prev => ({ ...prev, content: newContent }));
  };

  const addContentField = () => {
    setFormData(prev => ({ ...prev, content: [...prev.content, ''] }));
  };

  const removeContentField = (index) => {
    const newContent = formData.content.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, content: newContent }));
  };

  const handleThumbnailChange = (e) => {
    setThumbnail(e.target.files[0]);
  };

  const handleAdditionalImagesChange = (e) => {
    const files = Array.from(e.target.files).slice(0, 5);
    setAdditionalImages(files);
  };

  const handlePdfChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setPdfFile(file);
      setRemovePdf(false);
    }
  };

  const handleRemovePdf = () => {
    setPdfFile(null);
    setRemovePdf(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const formDataToSend = new FormData();
      
      // Append basic fields
      formDataToSend.append('title', formData.title);
      formDataToSend.append('videoLink', formData.videoLink);
      formDataToSend.append('externalLink', formData.externalLink);
      formDataToSend.append('publishDate', formData.publishDate);
      formDataToSend.append('author', formData.author);
      formDataToSend.append('tags', formData.tags);
      
      // Append content paragraphs
      formData.content.forEach((para) => {
        if (para && para.trim() !== '') {
          formDataToSend.append('content', para);
        }
      });
      
      // Ensure at least one content field exists
      if (formData.content.length === 0 || formData.content.every(para => !para || para.trim() === '')) {
        formDataToSend.append('content', '');
      }

      // Append thumbnail if exists
      if (thumbnail) {
        formDataToSend.append('thumbnail', thumbnail);
      } else if (!blog) {
        throw new Error('Thumbnail is required for new blogs');
      }
      
      // Append additional images
      additionalImages.forEach((image) => {
        formDataToSend.append('additionalImages', image);
      });

      // Append PDF file if exists
      if (pdfFile) {
        formDataToSend.append('pdfFile', pdfFile);
      }

      // Handle PDF removal
      if (removePdf) {
        formDataToSend.append('removePdf', 'true');
      }

      if (blog) {
        await blogService.updateBlog(blog._id, formDataToSend);
      } else {
        await blogService.createBlog(formDataToSend);
      }

      onSave();
      onClose();
    } catch (err) {
      console.error('Error:', err);
      setError(
        err.response?.data?.message || 
        err.response?.data?.error || 
        err.message || 
        'Failed to save blog. Please check all required fields.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && <div className="text-red-500">{error}</div>}
      
      {/* Show current thumbnail if editing */}
      {blog && blog.thumbnail && (
        <div>
          <label className="block text-sm font-medium text-gray-700">Current Thumbnail</label>
          <img 
            src={getImageSrc(blog.thumbnail)} 
            alt="Current thumbnail" 
            className="mt-2 h-32 object-cover rounded"
          />
        </div>
      )}
      
      <div>
        <label className="block text-sm font-medium text-gray-700">Thumbnail</label>
        <input
          type="file"
          accept="image/*"
          onChange={handleThumbnailChange}
          className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-[#166534] file:text-white hover:file:bg-[#0e3d1f]"
          required={!blog}
        />
      </div>

      {/* Show current additional images if editing */}
      {blog && blog.additionalImages && blog.additionalImages.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700">Current Additional Images</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {blog.additionalImages.map((img, index) => (
              <img 
                key={index}
                src={getImageSrc(img)} 
                alt={`Additional ${index + 1}`} 
                className="h-24 w-24 object-cover rounded"
              />
            ))}
          </div>
        </div>
      )}
      
      <div>
        <label className="block text-sm font-medium text-gray-700">Additional Images (max 5)</label>
        <input
          type="file"
          accept="image/*"
          multiple
          onChange={handleAdditionalImagesChange}
          className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-[#166534] file:text-white hover:file:bg-[#0e3d1f]"
        />
        {additionalImages.length > 0 && (
          <p className="mt-1 text-sm text-gray-500">
            {additionalImages.length} image(s) selected (max 5 allowed)
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Title *</label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleChange}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#166534] focus:border-[#166534]"
          required
          placeholder="Enter blog title"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Content *</label>
        {formData.content.map((para, index) => (
          <div key={index} className="mt-2 flex">
            <textarea
              value={para}
              onChange={(e) => handleContentChange(index, e.target.value)}
              className="flex-1 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#166534] focus:border-[#166534]"
              rows="3"
              required
              placeholder={`Paragraph ${index + 1}`}
            />
            {formData.content.length > 1 && (
              <button
                type="button"
                onClick={() => removeContentField(index)}
                className="ml-2 text-red-500 hover:text-red-700"
              >
                Remove
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          onClick={addContentField}
          className="mt-2 inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-[#166534] hover:bg-[#0e3d1f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#166534]"
        >
          Add Paragraph
        </button>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Video Link (optional)</label>
        <input
          type="url"
          name="videoLink"
          value={formData.videoLink}
          onChange={handleChange}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#166534] focus:border-[#166534]"
          placeholder="https://example.com/video"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">External Link (optional)</label>
        <input
          type="url"
          name="externalLink"
          value={formData.externalLink}
          onChange={handleChange}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#166534] focus:border-[#166534]"
          placeholder="https://example.com"
        />
      </div>

      {/* PDF File Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700">PDF Attachment (optional)</label>
        {blog && blog.pdfFile && !removePdf && (
          <div className="mt-2 flex items-center gap-2">
            <a 
              href={blog.pdfFile.startsWith('/') ? `${process.env.REACT_APP_API_BASE_URL || ''}${blog.pdfFile}` : blog.pdfFile}
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#166534] hover:underline text-sm"
            >
              📄 Current PDF attached
            </a>
            <button
              type="button"
              onClick={handleRemovePdf}
              className="text-red-500 hover:text-red-700 text-sm"
            >
              Remove
            </button>
          </div>
        )}
        <input
          type="file"
          accept="application/pdf"
          onChange={handlePdfChange}
          className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-[#166534] file:text-white hover:file:bg-[#0e3d1f]"
        />
        {pdfFile && (
          <p className="mt-1 text-sm text-gray-500">
            New PDF selected: {pdfFile.name}
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Publish Date *</label>
        <input
          type="date"
          name="publishDate"
          value={formData.publishDate}
          onChange={handleChange}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#166534] focus:border-[#166534]"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Author *</label>
        <input
          type="text"
          name="author"
          value={formData.author}
          onChange={handleChange}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#166534] focus:border-[#166534]"
          required
          placeholder="Author name"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Tags * (comma separated)</label>
        <input
          type="text"
          name="tags"
          value={formData.tags}
          onChange={handleChange}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#166534] focus:border-[#166534]"
          required
          placeholder="tag1, tag2, tag3"
        />
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#166534]"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#166534] hover:bg-[#0e3d1f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#166534] disabled:opacity-50"
        >
          {isLoading ? 'Saving...' : blog ? 'Update Blog' : 'Create Blog'}
        </button>
      </div>
    </form>
  );
};

export default BlogForm;